import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AlloyButton, QuantityInput } from 'cat-ecommerce-alloy';
import ProductSaveToListButton from './ProductSaveToListButton';
import { decrement, increment } from './utils';
import useAnalytics from '../../../../../hooks/useAnalytics';
import { productShape } from '../../../../../hooks/useAnalytics/useAnalytics.proptypes';
import CustomizeAction from '../CustomizeAction';
import { CatButton } from '@blocks-react/components/Button';
import styles from '../productPrimaryActions.scss';
import { BUTTON_SIZES, BUTTON_VARIANTS } from '@app/constants/commonConstants';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const AddToCartActions = ({
  addToCartIcon,
  addToCartClassName,
  children,
  gaListName = '', // this parameter is for the GA list.
  isDisabled,
  isPackage,
  isPackageWithItemAssociated,
  isPlural,
  onAddToCart,
  productClickDetails,
  quantity,
  quantityError,
  setQuantity,
  setQuantityError,
  showQuantityInput,
  showSaveToList,
  showSmallVariant,
  selectedQuantity,
  isBundle,
  showAddtoCartButtonOnly = false
}) => {
  const [t] = useTranslation();

  const defaultButtonLabel = isPlural
    ? t('CAT_PD_ADD_ALL_TO_CART')
    : t('ADD_TO_CART');

  const buttonText = isPackage ? t('KITS_ADD_KIT_TO_CART') : defaultButtonLabel;

  const current = useSelector(s => s.products?.current);
  const isCustomizable = useSelector(
    s => s.products.byId?.[current]?.isCustomizable
  );
  const packageQty =
    useSelector(s => s.products.priceAndAvailability?.[current]?.packageQty) ??
    1;
  const priceAndAvailability = useSelector(
    s => s.products.priceAndAvailability
  );
  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';
  const validator = useCallback(
    val => {
      const intVal = parseInt(val);
      const isValid = intVal % packageQty === 0 && intVal !== 0;
      const errMsg = `${t('SC_PKG_QTY_PART_ERROR2')} (${packageQty})`;
      return isValid ? null : errMsg;
    },
    [packageQty, t]
  );
  const { fireProductClickEvent, fireAddToCartEvent } = useAnalytics();

  // This is a band-aid because external state updates aren't currently
  // handled correctly within alloy's Inputs
  useEffect(() => {
    if (validator && setQuantityError) {
      setQuantityError(validator(quantity));
    }
  }, [quantity, setQuantityError, validator]);

  const products = Array.isArray(productClickDetails)
    ? productClickDetails
    : [productClickDetails];

  const productDetails = products?.map((product, index) => {
    const discountedAmount =
      priceAndAvailability?.[product?.partNumber]?.discountedAmount;
    const itemCatEntryId =
      priceAndAvailability?.[product?.partNumber]?.itemCatEntryId;
    const totalPrice =
      priceAndAvailability?.[product?.partNumber]?.unformattedTotalPrice;
    return {
      ...product,
      discountedAmount: convertFormattedNumberToUnformatted(
        discountedAmount || ''
      ),
      itemCatEntryId: itemCatEntryId || '',
      position: product?.position || 1,
      totalPrice
    };
  });

  const handleClick = () => {
    onAddToCart();
    if (!isBundle) {
      if (gaListName) {
        fireProductClickEvent({ list: gaListName, products, dealerStore });
      }
      fireAddToCartEvent({
        products: productDetails,
        totalPrice: convertFormattedNumberToUnformatted(
          productDetails?.[0]?.totalPrice || ''
        ),
        dealerStore
      });
    }
  };

  const getAddToCartButton = () =>
    showAddtoCartButtonOnly ? (
      <CatButton
        data-testid="AddKitToCartButton"
        onclick={handleClick}
        size={BUTTON_SIZES.SM}
        variant={BUTTON_VARIANTS.PRIMARY}
      >
        {buttonText}
      </CatButton>
    ) : (
      <AlloyButton
        buttonType={showSmallVariant ? 'secondary' : 'primary'}
        className={addToCartClassName ?? 'w-100'}
        disabled={isDisabled || !!quantityError}
        onClick={handleClick}
        size={showSmallVariant ? 'dynamic' : 'large'}
      >
        {addToCartIcon ?? buttonText}
      </AlloyButton>
    );

  const isCustomizeKit = isPackageWithItemAssociated && isCustomizable;

  const isCustomizablePackage =
    (isPackage && isCustomizeKit) || (isBundle && isPackageWithItemAssociated);

  return (
    <>
      <div
        className={cx('align-items-end', {
          row: !showSmallVariant,
          'd-flex align-items-center flex-row-reverse': showAddtoCartButtonOnly
        })}
      >
        {showQuantityInput && !showSmallVariant && (
          <div className="col-12 col-lg-auto py-1">
            <QuantityInput
              className={styles['product-quantity-picker']}
              decrement={prev => decrement(prev, packageQty)}
              disabled={isDisabled}
              enableButtons
              error={quantityError}
              id="productQuantity"
              increment={prev => increment(prev, packageQty)}
              maxValue="99999"
              minValue={packageQty}
              setError={setQuantityError}
              setValue={setQuantity}
              type="number"
              value={quantity}
              validator={validator}
              label={t('QUANTITY1')}
            />
          </div>
        )}
        <div
          data-testid="addToCartContainer"
          className={cx({
            'pt-lg-4 mt-lg-2 py-2 me-2 col-12 col-md-auto pe-lg-0':
              !showSmallVariant,
            'py-1 me-2': showSmallVariant && !showAddtoCartButtonOnly,
            'ps-lg-2': showQuantityInput && !showSmallVariant
          })}
        >
          {getAddToCartButton()}
        </div>
        {isCustomizablePackage && (
          <CustomizeAction
            showAddtoCartButtonOnly={showAddtoCartButtonOnly}
            isBundle={isBundle}
            selectedQuantity={selectedQuantity}
            disabled={isDisabled || !!quantityError}
            gaListName={gaListName}
            quantity={quantity}
          />
        )}
        {!isPackage && !isBundle && showSaveToList && !showSmallVariant && (
          <div
            data-testid="saveToListContainer"
            className={cx('col-12 col-lg py-3', {
              'pt-lg-4 mt-lg-3': !showSmallVariant
            })}
          >
            <ProductSaveToListButton />
          </div>
        )}
      </div>
      {isPackage && !isBundle && showSaveToList && !showSmallVariant && (
        <div data-testid="saveToListContainer" className={cx('ps-lg-1')}>
          <ProductSaveToListButton />
        </div>
      )}
      {children}
    </>
  );
};

AddToCartActions.propTypes = {
  addToCartIcon: PropTypes.node,
  addToCartClassName: PropTypes.string,
  children: PropTypes.node,
  gaListName: PropTypes.string,
  isDisabled: PropTypes.bool,
  isPlural: PropTypes.bool,
  onAddToCart: PropTypes.func.isRequired,
  productClickDetails: PropTypes.oneOfType([
    productShape,
    PropTypes.arrayOf(productShape)
  ]),
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  quantityError: PropTypes.string,
  setQuantity: PropTypes.func,
  setQuantityError: PropTypes.func,
  showQuantityInput: PropTypes.bool,
  showSaveToList: PropTypes.bool,
  showSmallVariant: PropTypes.bool,
  isPackage: PropTypes.bool,
  isPackageWithItemAssociated: PropTypes.bool,
  selectedQuantity: PropTypes.number,
  isBundle: PropTypes.bool,
  showAddtoCartButtonOnly: PropTypes.bool
};

export default AddToCartActions;
